import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import { MaterialIcons as Icon } from '@expo/vector-icons';
import { useNavigation, useTheme } from '@react-navigation/native';
import { Theme } from '@theme';
import { Button, Header, SearchBar, ListItemSkeletonList } from '@components';
import { usePaginateHashtags, useScreen } from '@hooks';
import type { Hashtag } from '@hooks';
import { getCommonStyles } from '@utils'
import EditHashtag from './EditHashtag';
import HashtagDetails from './HashtagDetails';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLanguage } from '@context';

export const Hashtags: React.FC = () => {
  const navigation = useNavigation<any>();
  const theme = useTheme();
  const {isWeb, isMobile} = useScreen();
  const { t } = useLanguage();
  const commonStyles = getCommonStyles(theme);
  const styles = getStyles(theme);

  const [searchText, setSearchText] = useState('');
  
  const { flatData: hashtags, fetchNextPage, hasNextPage, isFetchingNextPage, totalCount, isLoading } = usePaginateHashtags(searchText || undefined);

  const [hashtagId, setHashtagId] = useState<string | null>(hashtags[0]?.id || null);
  const [isEdit, setIsEdit] = useState(false);

  const navigateToHashtagDetails = (hashtag: Hashtag) => {
    navigation.navigate('HashtagDetails', { hashtagId: hashtag.id });
  };

  const handleAddHashtag = () => {
    setIsEdit(false);
    navigation.navigate('AddHashtag');
  };

  const renderHashtagItem = ({ item: hashtag }: { item: Hashtag }) => (
    <TouchableOpacity
      style={[styles.hashtagItem, !isMobile && hashtagId === hashtag.id && {backgroundColor: theme.colors.grey10}]}
      onPress={() => {
        if(isMobile) {
          navigateToHashtagDetails(hashtag);
        } else {
          setIsEdit(false);
          setHashtagId(hashtag.id);
        }
      }}
      activeOpacity={0.7}
    >
      <View style={styles.hashtagContent}>
        <View style={styles.hashtagInfo}>
          <Text style={styles.hashtagName}>{hashtag.name}</Text>
          <Text style={styles.hashtagSubtitle}>{t('common.personCount', { count: hashtag.contactAccountIds?.length || 0 })}</Text>
        </View>
      </View>
      <Icon name="chevron-right" size={24} color="#8E8E93" />
    </TouchableOpacity>
  );

  const handleEndReached = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={commonStyles.flex}>
        <Header
          title={t('filters.hashtag')}
          iconLeft={isWeb? undefined : "arrowBack"}
          iconRight={isMobile? "plus" : undefined}
          onPressRight={handleAddHashtag}
        />
        <View style={[commonStyles.rowBetween, commonStyles.alignStart]}>
          <View style={commonStyles.flex}>
            <View style={[styles.searchContainer, !isMobile && commonStyles.searchContainerMaxWidth]}>
              <SearchBar value={searchText} onChangeText={setSearchText} />
            </View>

            <Text style={styles.sectionTitle}>{t('common.resultsCount', { count: totalCount })}</Text>
          </View>
          {!isMobile && <Button isSmall style={[styles.buttonAdd, !isMobile && commonStyles.alignSelfEnd]}  title={t('hashtagsScreen.newHashtag')} textColor={theme.colors.primary} iconName="plus" iconColor={theme.colors.primary} onPress={handleAddHashtag} />}
        </View>
        <View style={[commonStyles.flex, commonStyles.rowGapLg, commonStyles.alignStart]}>
          {isLoading ? (
            <View style={[isMobile? styles.hashtagsList : commonStyles.flex315, commonStyles.fullHeight]}>
              <ListItemSkeletonList count={8} />
            </View>
          ) : (
            <FlatList
              data={hashtags}
              renderItem={renderHashtagItem}
              keyExtractor={(item) => item.id}
              onEndReached={handleEndReached}
              onEndReachedThreshold={0.5}
              showsVerticalScrollIndicator={false}
              style={[isMobile? styles.hashtagsList : commonStyles.flex315, commonStyles.fullHeight]}
            />
          )}
          {!isMobile && !!hashtagId && <View style={styles.columnDivider} />}
          {!isMobile && !!hashtagId && !isEdit && <View style={[commonStyles.flex, commonStyles.fullHeight]}>
            <HashtagDetails params={{ hashtagId: hashtagId }} onEdit={() => setIsEdit(true)} />
          </View>}
          {!isMobile && !!hashtagId && isEdit && <View style={[commonStyles.flex, commonStyles.fullHeight]}>
            <EditHashtag hashtagId={hashtagId} onBack={() => setIsEdit(false)} onDeleted={() => { setIsEdit(false); setHashtagId(null); }} />
          </View>}
        </View>
      </View>
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#fff',
  },
  searchContainer: {
    paddingHorizontal: theme.spacing.md,
    backgroundColor: '#fff',
  },
  buttonAdd: {
    backgroundColor: theme.colors.white,
    marginRight: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: 13,
    color: '#8E8E93',
    fontWeight: '400',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  hashtagsList: {
    flex: 1,
  },
  // Thin vertical line separating the list column from the detail column (web).
  columnDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: theme.colors.grey,
  },
  hashtagItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E5EA',
  },
  hashtagContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  hashtagIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    backgroundColor: '#007AFF',
  },
  hashtagSymbol: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  hashtagInfo: {
    flex: 1,
  },
  hashtagName: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000',
    marginBottom: 4,
  },
  hashtagSubtitle: {
    fontSize: 15,
    color: '#8E8E93',
    fontWeight: '400',
  },
}); 